import { motion } from 'motion/react'
import { usePage, useTimeline } from '@/lib/queries'
import { fileUrl } from '@/lib/pb'
import { plainText } from '@/lib/utils'
import { BrandMask, Container, MeshTexture } from '@/components/site/primitives'
import { PageHero } from '@/components/site/page-hero'
import { LoadingBlock } from '@/components/ui/feedback'
import { usePageMeta } from '@/components/layout/site-layout'

export default function StoryPage() {
  const { data: page, isLoading } = usePage('story')
  const { data: timeline = [] } = useTimeline()

  usePageMeta(page?.title ?? 'Story', page?.seo_description || plainText(page?.body ?? '', 200))

  if (isLoading)
    return <LoadingBlock className="min-h-[70vh] pt-[calc(var(--header-h)+4rem)]" label="Loading" />

  return (
    <>
      <PageHero
        kicker={page?.kicker || 'Since 2021'}
        rail="Our story"
        title={page?.title || 'Our Story'}
        subtitle={page?.subtitle}
        image={fileUrl(page, page?.hero_image, '1600x0')}
      />

      {page?.body ? (
        <section className="border-hairline bg-ink border-b py-14 md:py-24">
          <Container size="narrow">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="prose-viking text-[1.125rem]"
              dangerouslySetInnerHTML={{ __html: page.body }}
            />
          </Container>
        </section>
      ) : null}

      {timeline.length > 0 ? (
        <section className="bg-ink-sunken relative overflow-hidden py-16 md:py-28">
          <MeshTexture opacity={0.05} />
          <BrandMask
            src="illustration"
            className="text-blood-deep pointer-events-none absolute -right-24 bottom-0 hidden h-[36rem] w-[32rem] opacity-25 lg:block"
          />

          <Container className="relative">
            <div className="mb-12 flex items-center gap-3">
              <span className="bg-blood h-px w-10" aria-hidden />
              <span className="eyebrow text-blood">Milestones</span>
            </div>

            <ol className="relative max-w-3xl">
              {/* The spine the year markers hang from. */}
              <span
                className="bg-hairline absolute top-2 bottom-2 left-[4.25rem] w-px md:left-[6.25rem]"
                aria-hidden
              />

              {timeline.map((entry, index) => {
                const image = fileUrl(entry, entry.image, '800x0')
                return (
                  <motion.li
                    key={entry.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: index * 0.05 }}
                    className="relative grid grid-cols-[4.25rem_1fr] gap-6 pb-12 md:grid-cols-[6.25rem_1fr] md:gap-10"
                  >
                    <div className="relative">
                      <span className="display text-blood text-2xl leading-none md:text-3xl">
                        {entry.year}
                      </span>
                      <span
                        className="bg-blood absolute top-2 -right-[0.3125rem] size-2.5 rotate-45"
                        aria-hidden
                      />
                    </div>

                    <div className="pb-2">
                      <h3 className="condensed text-bone text-xl leading-tight font-bold tracking-[0.04em] md:text-2xl">
                        {entry.title}
                      </h3>
                      {entry.body ? (
                        <p className="text-bone-dim mt-3 max-w-xl leading-relaxed">{entry.body}</p>
                      ) : null}
                      {image ? (
                        <img
                          src={image}
                          alt=""
                          loading="lazy"
                          className="mt-5 aspect-16/9 w-full max-w-lg object-cover"
                        />
                      ) : null}
                    </div>
                  </motion.li>
                )
              })}
            </ol>
          </Container>
        </section>
      ) : null}
    </>
  )
}
